import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import SkipToContent from "@/components/a11y/SkipToContent";
import {
  ArrowLeft,
  BarChart3,
  ChevronDown,
  CreditCard,
  DollarSign,
  FileText,
  Files,
  Flag,
  Home,
  KeyRound,
  LineChart,
  Mail,
  Menu,
  MessageSquare,
  Percent,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Handshake,
  Store,
  Tag,
  Target,
  TrendingDown,
  TrendingUp,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import ThemeToggle from "@/components/v2/ThemeToggle";

/**
 * Admin shell — left sidebar nav, top bar with "ADMIN" pill + user dropdown.
 *
 * Wave 1 (Executive Foundation) — migrated from legacy V2 tokens to
 * `--ds-*` tokens. Every previous hardcoded hex literal (`#B794F4`,
 * `#FCD34D`, `rgba(93,80,200,...)`, `rgba(183,148,244,...)`) is replaced
 * by a `--ds-*` token. Testid contract preserved byte-exact so existing
 * downstream tests + testing-agent selectors keep working.
 *
 * Executive-facing rationale: this shell is inherited by every admin
 * route. Migrating it converts 40 admin surfaces from V2 chrome to DS
 * chrome for free — a first-time user cannot distinguish AdminLayout
 * from LandingV2's design language anymore.
 */
// Each entry declares the permission required to see it; null = always visible.
// Owner sees everything regardless.
const NAV = [
  { to: "/admin", label: "Overview", icon: BarChart3, end: true, permission: null },
  { to: "/admin/users", label: "Users", icon: Users, permission: "view_all_users" },
  { to: "/admin/team", label: "Team", icon: UserCog, permission: "manage_team" },
  { to: "/admin/partners", label: "Partners", icon: Handshake, permission: "view_funding_partners" },
  { to: "/admin/marketplace", label: "Marketplace", icon: Store, permission: null },
  { to: "/admin/grants", label: "Grants", icon: Target, permission: "edit_scoring_rubric" },
  { to: "/admin/letters", label: "Letters", icon: Mail, permission: "edit_dispute_letters" },
  { to: "/admin/credit-reports", label: "Credit Reports", icon: CreditCard, permission: "view_credit_reports" },
  { to: "/admin/bank-statements", label: "Bank Statements", icon: Files, permission: "view_credit_reports" },
  { to: "/admin/mailed-letters", label: "Mailed Letters", icon: Mail, permission: "view_credit_reports" },
  { to: "/admin/plans", label: "Plans", icon: Tag, permission: "view_billing" },
  { to: "/admin/coupons", label: "Coupons", icon: Percent, permission: "view_billing" },
  { to: "/admin/churn", label: "Churn", icon: TrendingDown, permission: "view_billing" },
  { to: "/admin/revenue", label: "Revenue", icon: TrendingUp, permission: "view_all_users" },
  { to: "/admin/billing", label: "Billing", icon: DollarSign, permission: "view_billing" },
  { to: "/admin/site-settings", label: "Site Settings", icon: Settings, permission: "edit_site_settings" },
  { to: "/admin/compliance", label: "Compliance", icon: Shield, permission: "view_credit_reports" },
  { to: "/admin/system-config", label: "System Config", icon: ShieldCheck, permission: "edit_site_settings" },
  { to: "/admin/feature-flags", label: "Feature Flags", icon: Flag, permission: "toggle_feature_flags" },
  { to: "/admin/scoring-rubric", label: "Scoring Rubric", icon: SlidersHorizontal, permission: "edit_scoring_rubric" },
  { to: "/admin/mortgage-config", label: "Mortgage Config", icon: Home, permission: "edit_scoring_rubric" },
  { to: "/admin/credit-catalog", label: "Credit Catalog", icon: CreditCard, permission: "edit_scoring_rubric" },
  { to: "/admin/forecast-config", label: "Forecast Config", icon: SlidersHorizontal, permission: "edit_scoring_rubric" },
  { to: "/admin/credit-tier-rates", label: "Credit Tier Rates", icon: CreditCard, permission: "edit_scoring_rubric" },
  { to: "/admin/ai-logs", label: "AI Logs", icon: FileText, permission: "view_ai_audit_logs" },
  { to: "/admin/email-logs", label: "Email Logs", icon: Mail, permission: "view_audit_logs" },
  { to: "/admin/email-templates", label: "Email Templates", icon: Mail, permission: "edit_site_settings" },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ShieldCheck, permission: "view_audit_logs" },
  { to: "/admin/beta-codes", label: "Early Access Codes", icon: KeyRound, permission: "view_all_users" },
  { to: "/admin/beta-waitlist", label: "Beta Waitlist", icon: MessageSquare, permission: "view_all_users" },
  { to: "/admin/beta-feedback", label: "Feedback", icon: MessageSquare, permission: "view_all_users" },
  { to: "/admin/analytics/funnel", label: "Analytics", icon: LineChart, permission: "view_all_users" },
];

function useEnvironmentBadge() {
  const [env, setEnv] = useState({ environment_label: null, stripe_mode: null, maintenance_mode: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/system-config/environment");
        if (!cancelled) setEnv(data || {});
      } catch {/* tolerated — banner just won't show */}
    })();
    return () => { cancelled = true; };
  }, []);
  return env;
}

// Nav-link styling extracted so the desktop + mobile drawer emit identical DOM.
const navLinkClass = ({ isActive }) =>
  `group flex items-center gap-2.5 rounded-[var(--ds-radius-md)] px-3 py-2 text-[13px] transition-colors ${
    isActive
      ? "text-[var(--ds-color-ink)]"
      : "text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
  }`;

const navLinkStyle = ({ isActive }) => ({
  background: isActive ? "var(--ds-color-accent-soft)" : "transparent",
  borderLeft: isActive ? "2px solid var(--ds-color-accent)" : "2px solid transparent",
  paddingLeft: isActive ? 10 : 12,
  transitionDuration: "var(--ds-duration-fast)",
});

export default function AdminLayout({ title, subtitle, children, actions }) {
  const { user, logout } = useAuth();
  const { has, isOwner } = useAdminPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const env = useEnvironmentBadge();
  // V1.1a — sub-pages (anything other than /admin overview) get a Back-to-
  // Overview affordance in both the mobile header and the page footer.
  const isOverview = location.pathname === "/admin" || location.pathname === "/admin/";

  const visibleNav = NAV.filter((item) => {
    if (!item.permission) return true;
    if (isOwner) return true;
    return has(item.permission);
  });

  return (
    <div
      data-testid="admin-layout"
      className="min-h-screen flex overflow-x-hidden"
      style={{
        background: "var(--ds-color-admin-surface)",
        color: "var(--ds-color-ink)",
        paddingTop: env.environment_label === "sandbox" ? 26 : 0,
      }}
    >
      <SkipToContent />
      {env.environment_label === "sandbox" && (
        <div
          data-testid="admin-sandbox-banner"
          className="fixed top-0 left-0 right-0 z-40 text-center text-[10px] uppercase tracking-[0.2em] py-1.5"
          style={{
            background: "var(--ds-color-warning-soft)",
            color: "var(--ds-color-warning)",
            borderBottom: "1px solid var(--ds-color-warning)",
          }}
        >
          Sandbox mode · Stripe {env.stripe_mode || "stub"} · No real charges, no real emails
        </div>
      )}
      {/* Mobile drawer overlay — visible only when mobileNavOpen && <md */}
      {mobileNavOpen && (
        <div
          data-testid="admin-mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/55"
        />
      )}
      {/* Off-canvas mobile drawer — mirrors desktop sidebar content */}
      <aside
        data-testid="admin-mobile-drawer"
        className={`md:hidden fixed top-0 left-0 h-full w-72 max-w-[85vw] z-50 border-r border-[var(--ds-color-border)] px-4 py-6 flex flex-col transition-transform duration-200 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "var(--ds-color-surface)" }}
      >
        <div className="flex items-center justify-between mb-7">
          <Link
            to="/admin"
            data-testid="admin-mobile-drawer-brand"
            onClick={() => setMobileNavOpen(false)}
            className="flex items-center gap-2 px-2"
          >
            <ShieldAlert
              className="w-5 h-5"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden
            />
            <div>
              <div
                className="text-[16px] leading-tight text-[var(--ds-color-ink)]"
                style={{ fontFamily: "var(--ds-font-display)" }}
              >
                Informed
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">
                Admin Console
              </div>
            </div>
          </Link>
          <button
            type="button"
            data-testid="admin-mobile-drawer-close"
            onClick={() => setMobileNavOpen(false)}
            className="p-1.5 rounded-[var(--ds-radius-sm)] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto -mr-2 pr-2">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMobileNavOpen(false)}
              data-testid={`admin-mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={navLinkClass}
              style={navLinkStyle}
            >
              <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <Link
          to="/dashboard"
          onClick={() => setMobileNavOpen(false)}
          data-testid="admin-mobile-drawer-back-to-app"
          className="mt-6 inline-flex items-center gap-2 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] px-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to user app
        </Link>
      </aside>

      {/* Desktop sidebar */}
      <aside
        data-testid="admin-sidebar"
        className="hidden md:flex w-64 flex-shrink-0 flex-col border-r border-[var(--ds-color-admin-border)] px-4 py-6"
        style={{ background: "var(--ds-color-admin-surface-muted)" }}
      >
        <Link to="/admin" data-testid="admin-sidebar-brand" className="flex items-center gap-2 mb-7 px-2">
          <ShieldAlert
            className="w-5 h-5"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden
          />
          <div>
            <div
              className="text-[16px] leading-tight text-[var(--ds-color-ink)]"
              style={{ fontFamily: "var(--ds-font-display)" }}
            >
              Informed
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">
              Admin Console
            </div>
          </div>
        </Link>
        <nav className="flex-1 space-y-1">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={navLinkClass}
              style={navLinkStyle}
            >
              <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <Link
          to="/dashboard"
          data-testid="admin-sidebar-back-to-app"
          className="mt-6 inline-flex items-center gap-2 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] px-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to user app
        </Link>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          data-testid="admin-topbar"
          className="flex items-center justify-between gap-4 px-5 md:px-8 py-4 border-b border-[var(--ds-color-admin-border)]"
          style={{ background: "var(--ds-color-admin-surface-muted)" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="admin-mobile-menu-toggle"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] flex-shrink-0"
              aria-label="Open navigation menu"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span
                  data-testid="admin-pill"
                  className="inline-flex items-center gap-1 rounded-[var(--ds-radius-pill)] px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em]"
                  style={{
                    background: "var(--ds-color-accent-soft)",
                    color: "var(--ds-color-accent-strong)",
                    border: "1px solid var(--ds-color-accent)",
                  }}
                >
                  <ShieldAlert className="w-3 h-3" aria-hidden="true" /> Admin
                </span>
                {title && (
                  <h1
                    data-testid="admin-page-title"
                    className="text-[22px] md:text-[26px] text-[var(--ds-color-ink)] truncate font-semibold"
                    style={{ fontFamily: "var(--ds-font-display)", letterSpacing: "-0.015em" }}
                  >
                    {title}
                  </h1>
                )}
              </div>
              {subtitle && (
                <p
                  data-testid="admin-page-subtitle"
                  className="text-[12px] text-[var(--ds-color-ink-muted)] mt-1 truncate"
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <div className="relative">
              <button
                type="button"
                data-testid="admin-user-menu-trigger"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] px-3 py-1.5 text-[13px] hover:bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)]"
              >
                <span className="hidden md:inline truncate max-w-[180px]">{user?.email}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[var(--ds-color-ink-muted)]" />
              </button>
              {menuOpen && (
                <div
                  data-testid="admin-user-menu"
                  className="absolute right-0 mt-1 w-56 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] py-1 z-30 shadow-[var(--ds-shadow-raised)]"
                  style={{ background: "var(--ds-color-surface)" }}
                >
                  <Link
                    to="/dashboard"
                    data-testid="admin-user-menu-dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="block w-full text-left px-3 py-2 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)]"
                  >
                    Back to user app
                  </Link>
                  <div
                    className="px-3 py-2 flex items-center justify-between gap-3 text-[13px] text-[var(--ds-color-ink-muted)]"
                    data-testid="admin-user-menu-theme-row"
                  >
                    <span>Theme</span>
                    <ThemeToggle compact />
                  </div>
                  <button
                    type="button"
                    data-testid="admin-user-menu-logout"
                    onClick={async () => {
                      setMenuOpen(false);
                      await logout();
                      navigate("/login");
                    }}
                    className="block w-full text-left px-3 py-2 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)]"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main
          id="main-content"
          data-testid="admin-main"
          className="flex-1 px-5 md:px-8 py-7 overflow-x-hidden"
        >
          {/* V1.1a — mobile-only Back to Overview pill (sidebar is hidden <md). */}
          {!isOverview && (
            <Link
              to="/admin"
              data-testid="admin-mobile-back-to-overview"
              className="md:hidden inline-flex items-center gap-1.5 mb-4 text-[12px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Admin Overview
            </Link>
          )}
          {children}
          {/* V1.1a — footer Back link on every sub-page. */}
          {!isOverview && (
            <div className="mt-12 pt-6 border-t border-[var(--ds-color-border)] flex items-center justify-between gap-4 flex-wrap">
              <Link
                to="/admin"
                data-testid="admin-footer-back-to-overview"
                className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
              >
                <ArrowLeft className="w-3 h-3" /> Back to Admin Overview
              </Link>
              <Link
                to="/dashboard"
                data-testid="admin-footer-back-to-app"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
              >
                Back to user app
              </Link>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
